"use client";

import { useTranslation } from "react-i18next";

/**
 * STORY-106 — "Continue with Google" button.
 *
 * Renders an outlined button with the Google G-mark. Click navigates
 * the browser to the API's `/v1/auth/google` endpoint, which redirects
 * to accounts.google.com. After consent the API redirects back to
 * `/auth/callback/google` with tokens in the URL hash; the callback
 * page calls saveTokens() and routes to /home.
 *
 * Renders nothing when NEXT_PUBLIC_API_URL is unset (e.g. broken dev
 * env) to avoid a click-through to a busted endpoint.
 */
export function GoogleSignInButton({
  label,
  className = "",
}: {
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const apiBase = process.env.NEXT_PUBLIC_API_URL;
  if (!apiBase) return null;

  const href = `${apiBase.replace(/\/$/, "")}/v1/auth/google`;
  const text = label ?? t("auth.continueWithGoogle");

  return (
    <a
      href={href}
      className={
        "inline-flex h-12 w-full items-center justify-center gap-3 rounded-md border px-6 font-semibold transition-colors hover:bg-[var(--color-surface-2)] " +
        className
      }
      style={{
        borderColor: "var(--color-border)",
        color: "var(--color-text)",
      }}
    >
      <GoogleGMark />
      {text}
    </a>
  );
}

function GoogleGMark() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
